import React, { useState } from "react";
import { CREATE_ARTICLE } from "models/mutations/article.mutations";
import { LOAD_ARTICLES } from "models/queries/article.queries";
import { useMutation } from "@apollo/client";
import { Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { store } from "models/store";
import { IAuthenticationState } from "sthinds.io-lib";
import MarkdownEditor from "../editor/markdown-editor.component";
import { toLocalDateString } from "../article-date";
import { articlePath } from "../article-url";
import "./create-article.component.scss";

function CreateArticleForm() {
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]); // Initialize date state
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const navigate = useNavigate();

  // TODO: move mutations and queries to an external injectable service
  const [createArticle] = useMutation(CREATE_ARTICLE, {
    // Otherwise the new post is missing from the index until a full reload.
    refetchQueries: [{ query: LOAD_ARTICLES }],
  });

  const addArticle = async () => {
    const authorID = (
      store.getState() as { authentication: IAuthenticationState }
    )?.authentication._id;

    if (!authorID) {
      setSubmitError("You need to be signed in to publish.");
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const result = await createArticle({
        variables: {
          authorID,
          title,
          subtitle,
          date: toLocalDateString(date),
          text,
        },
      });

      const created = result.data?.createArticle;
      if (!created?._id) {
        throw new Error("The server did not return the new article.");
      }

      navigate(articlePath({ _id: created._id, title }));
    } catch (err) {
      // useMutation's `error` only lands on the *next* render, so reading it
      // straight after the call always saw the previous value — which is why
      // a failed publish looked identical to a successful one.
      setSubmitError(
        err instanceof Error ? err.message : "Could not publish the article."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="article-form">
      {/* No onSubmit: the buttons drive this explicitly, and letting the form
          submit natively reloads the page mid-mutation. */}
      <Form onSubmit={(event) => event.preventDefault()}>
        <Form.Group className="mb-3">
          <Form.Label>Title</Form.Label>
          <Form.Control
            onChange={(e) => {
              setTitle(e.target.value);
            }}
            type="text"
            value={title}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Subtitle</Form.Label>
          <Form.Control
            onChange={(e) => {
              setSubtitle(e.target.value);
            }}
            type="text"
            value={subtitle}
          />
        </Form.Group>
        <Form.Group className="mb-3 half-width">
          <Form.Label>Date</Form.Label>
          <Form.Control
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)} // Update date state
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Text</Form.Label>
          <MarkdownEditor value={text} onChange={setText} />
        </Form.Group>
        <div className="article-form-actions flex-row whole-width justify-end align-center">
          {submitError ? (
            <span className="article-form-error">{submitError}</span>
          ) : null}
          <Button
            variant="primary"
            type="button"
            onClick={addArticle}
            disabled={submitting || !title.trim() || !text.trim()}
          >
            {submitting ? "Publishing…" : "Publish"}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default CreateArticleForm;
